import { __ } from "@wordpress/i18n";
import { createRoot } from "react-dom/client";
import { Card } from "./ui/card";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";
import { TrendingUp, DollarSign, Calendar, Calculator } from "lucide-react";
import { useState } from "react";

const container = document.getElementById(
	"dm-gutenberg-block-partner-calculator-root",
);
if (container) {
    const attributes = JSON.parse(container.getAttribute("data-attributes") || "{}");
	createRoot(container).render(<View attributes={attributes} />);
}

export default function View({ attributes }) {
    
	const [dealsPerMonth, setDealsPerMonth] = useState([2]);
	const [unitsPerDeal, setUnitsPerDeal] = useState([50]);

	// Fixed pricing model
	const pricePerUnit = attributes.price_per_unit; // Fixed at $45/month
	const markupPercentage = attributes.markup; // Fixed at 50%

	// ARR Calculation: Units × Price per month × 12 months
	const avgDealSize = unitsPerDeal[0] * pricePerUnit * 12;
	const partnerMargin = markupPercentage / 100;

	// Earnings Calculations
	const monthlyRevenue = dealsPerMonth[0] * avgDealSize;
	const annualRevenue = monthlyRevenue * 12;
	const monthlyEarnings = monthlyRevenue * partnerMargin;
	const annualEarnings = annualRevenue * partnerMargin;

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const getInsightMessage = () => {
		if (dealsPerMonth[0] >= 8) {
			return __("🚀 Outstanding! You'll be earning well into six figures annually.", "dm-zenduit-custom-gutenberg-blocks");
		} else if (dealsPerMonth[0] >= 5) {
			return __("💪 Excellent potential! Strong six-figure income territory.", "dm-zenduit-custom-gutenberg-blocks");
		} else if (dealsPerMonth[0] >= 3) {
			return __("📈 Great start! Solid income with room to scale.", "dm-zenduit-custom-gutenberg-blocks");
		} else {
			return __("🌱 Perfect starting point! Even 1-2 deals makes a significant impact.", "dm-zenduit-custom-gutenberg-blocks");
		}
	};

	const getDealSizeInsight = () => {
		if (avgDealSize >= 30000) {
			return __("Enterprise-level deals - higher value, longer sales cycles", "dm-zenduit-custom-gutenberg-blocks");
		} else if (avgDealSize >= 20000) {
			return __("Mid-market sweet spot - good balance of size and velocity", "dm-zenduit-custom-gutenberg-blocks");
		} else {
			return __("SMB-focused deals - smaller size, faster closures", "dm-zenduit-custom-gutenberg-blocks");
		}
	};

	return (
		<section
			id="dm-gutenberg-block-partner-calculator"
			className="dm-gutenberg-block-partner-calculator-section"
		>
			<div className="container py-4">
				<div className="row g-6">
					{/* Deal Configuration */}
					<div className="col-lg-6">
						<Card>
							<div className="d-flex align-items-center mb-8">
								<Calculator
									style={{ width: 24, height: 24, color: "#2188d9" }}
								/>
								<h3 className="ms-3 fw-semibold mb-0">{__("Deal Configuration", "dm-zenduit-custom-gutenberg-blocks")}</h3>
							</div>
							<div className="mb-6">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<Label>{__("Units per Deal", "dm-zenduit-custom-gutenberg-blocks")}</Label>
									<span className="fw-bold text-primary">
										{unitsPerDeal[0]}
									</span>
								</div>
								<Slider
									value={unitsPerDeal}
									onValueChange={setUnitsPerDeal}
									max={attributes.max_units_per_deal}
									min={1}
									step={1}
								/>
								<div className="d-flex justify-content-between text-muted small mt-1">
									<span className="font-14">1 {__("units", "dm-zenduit-custom-gutenberg-blocks")}</span>
									<span className="font-14">{attributes.max_units_per_deal} {__("units", "dm-zenduit-custom-gutenberg-blocks")}</span>
								</div>
							</div>
							<div className="mb-6">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<Label>{__("Deals Per Month", "dm-zenduit-custom-gutenberg-blocks")}</Label>
									<span className="fw-bold text-primary">
										{dealsPerMonth[0]}
									</span>
								</div>
								<Slider
									value={dealsPerMonth}
									onValueChange={setDealsPerMonth}
									max={attributes.max_deals_per_month}
									min={1}
									step={1}
								/>
								<div className="d-flex justify-content-between text-muted small mt-1">
									<span className="font-14">1 {__("deal", "dm-zenduit-custom-gutenberg-blocks")}</span>
									<span className="font-14">{attributes.max_deals_per_month} {__("deals", "dm-zenduit-custom-gutenberg-blocks")}</span>
								</div>
							</div>
							<div className="p-4 bg-light border-radius-16 mb-6">
								<div className="fw-medium mb-2">{__("Pricing Model:", "dm-zenduit-custom-gutenberg-blocks")}</div>
								<div className="mb-2 d-flex justify-content-between small">
									<span className="text-muted">{__("Price per Unit/Month:", "dm-zenduit-custom-gutenberg-blocks")}</span>
									<span className="fw-semibold">${pricePerUnit}</span>
								</div>
								<div className="d-flex justify-content-between small">
									<span className="text-muted">{__("Your Markup:", "dm-zenduit-custom-gutenberg-blocks")}</span>
									<span className="fw-semibold">{markupPercentage}%</span>
								</div>
							</div>
							<div className="p-4 bg-primary-light border-radius-16 border border-primary mb-6">
								<div className="fw-medium mb-2">{__("Deal Value Calculation:", "dm-zenduit-custom-gutenberg-blocks")}</div>
								<div className="mb-1 d-flex justify-content-between small text-muted">
									<span>
										{unitsPerDeal[0]} {__("units:", "dm-zenduit-custom-gutenberg-blocks")} × ${pricePerUnit}/{__("month", "dm-zenduit-custom-gutenberg-blocks")}:
									</span>
									<span>
										{formatCurrency(unitsPerDeal[0] * pricePerUnit)}/mo
									</span>
								</div>
								<div className="mb-1 d-flex justify-content-between small text-muted">
									<span>× 12 {__("months", "dm-zenduit-custom-gutenberg-blocks")} (ARR):</span>
									<span className="fw-semibold text-dark">
										{formatCurrency(avgDealSize)}
									</span>
								</div>
								<div className="pt-3 border-top d-flex justify-content-between small">
									<span>{__("Your Profit per Deal:", "dm-zenduit-custom-gutenberg-blocks")}</span>
									<span className="fw-bold text-primary">
										{formatCurrency(avgDealSize * partnerMargin)}
									</span>
								</div>
								<div className="small text-muted mt-2">
									{getDealSizeInsight()}
								</div>
							</div>
							<div className="p-2 bg-primary-light border-radius-16 border border-primary">
								<p className="p-3 border-radius-16 small text-center mb-0">
									{getInsightMessage()}
								</p>
							</div>
						</Card>
					</div>
					{/* Earnings Results */}
					<div className="col-lg-6 d-flex flex-column gap-4">
						<Card className="border-left-primary">
							<div className="d-flex align-items-center mb-7">
								<div className="p-2 bg-primary bg-opacity-25 rounded-circle me-2">
									<Calendar
										style={{ width: 24, height: 24, color: "#2188d9" }}
									/>
								</div>
								<div className="ms-3">
									<h3 className="fw-semibold mb-0">{__("Monthly Earnings", "dm-zenduit-custom-gutenberg-blocks")}</h3>
									<p className="small text-muted mb-0">{__("Your monthly income", "dm-zenduit-custom-gutenberg-blocks")}</p>
								</div>
							</div>
							<div className="mb-2 d-flex justify-content-between small">
								<span className="text-muted">{__("Total Deal Value:", "dm-zenduit-custom-gutenberg-blocks")}</span>
								<span>{formatCurrency(monthlyRevenue)}</span>
							</div>
							<div className="mb-2 d-flex justify-content-between small">
								<span className="text-muted">{__("Your Markup", "dm-zenduit-custom-gutenberg-blocks")} ({markupPercentage}%):</span>
								<span>{formatCurrency(monthlyEarnings)}</span>
							</div>
							<div className="border-top pt-3 d-flex justify-content-between align-items-center">
								<span className="fw-semibold">{__("Monthly Income:", "dm-zenduit-custom-gutenberg-blocks")}</span>
								<span className="fs-4 fw-bold text-primary">
									{formatCurrency(monthlyEarnings)}
								</span>
							</div>
						</Card>
						<Card className="border-left-primary">
							<div className="d-flex align-items-center mb-7">
								<div className="p-2 bg-primary bg-opacity-25 rounded-circle me-2">
									<TrendingUp
										style={{ width: 24, height: 24, color: "#2188d9" }}
									/>
								</div>
								<div className="ms-3">
									<h3 className="fw-semibold mb-0">{__("Annual Projection", "dm-zenduit-custom-gutenberg-blocks")}</h3>
									<p className="small text-muted mb-0">{__("Your yearly potential", "dm-zenduit-custom-gutenberg-blocks")}</p>
								</div>
							</div>
							<div className="mb-2 d-flex justify-content-between small">
								<span className="text-muted">{__("Total ARR Generated:", "dm-zenduit-custom-gutenberg-blocks")}</span>
								<span>{formatCurrency(annualRevenue)}</span>
							</div>
							<div className="mb-2 d-flex justify-content-between small">
								<span className="text-muted">{__("Your Annual Markup:", "dm-zenduit-custom-gutenberg-blocks")}</span>
								<span>{formatCurrency(annualEarnings)}</span>
							</div>
							<div className="border-top pt-3 d-flex justify-content-between align-items-center">
								<span className="fw-semibold">{__("Annual Income:", "dm-zenduit-custom-gutenberg-blocks")}</span>
								<span className="fs-4 fw-bold text-primary">
									{formatCurrency(annualEarnings)}
								</span>
							</div>
						</Card>
						<Card className="bg-primary-light">
							<h3 className="fw-semibold mb-3 d-flex align-items-center">
								<DollarSign
									style={{ width: 20, height: 20, color: "#2188d9" }}
									className="me-2"
								/>
								{__("Income Breakdown", "dm-zenduit-custom-gutenberg-blocks")}
							</h3>
							<div className="row text-center">
								<div className="col">
									<div className="small text-muted">{__("Weekly Avg", "dm-zenduit-custom-gutenberg-blocks")}</div>
									<div className="fw-bold">
										{formatCurrency((monthlyEarnings * 12) / 52)}
									</div>
								</div>
								<div className="col">
									<div className="small text-muted">{__("Daily Avg", "dm-zenduit-custom-gutenberg-blocks")}</div>
									<div className="fw-bold">
										{formatCurrency(monthlyEarnings / 30)}
									</div>
								</div>
							</div>
						</Card>
						<Button
							size="xl"
							className={"w-100 border-radius-16 trigger-vue-form-modal product-request-demo" + attributes.button_class}
						>
							{ attributes.button_text }
						</Button>
					</div>
				</div>
			</div>
		</section>
	);
}
